import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { DrawerCloseButton } from '../drawer-close-button';
import { HeaderEntityDropdown } from '@flexo/atoms';
import { RIGHT_DRAWER } from '@flexo/reducers';

import { Billing } from '../../billing/billing';
import { useHeaderEntities } from '../../header/hooks/useHeaderEntities';
import styles from './right-drawer-content.module.scss';

interface EndUserInvoicesDrawerContentProps {
  drawerConfig?: { title?: string };
}

/**
 * FLEXO-6075 #2: end-user invoices drawer.
 * FLEXO-6087 #1b/#1c: title shows the selected site name with an "Invoices"
 * subtitle, and multi-site users get a site selector (like the mobile app).
 *
 * Reuses the existing `Billing` list (flat, tap to open the PDF) and the shared
 * `HeaderEntityDropdown` + `useHeaderEntities` site picker. Selecting a site
 * filters the list locally (no navigation); the "All sites" entry shows every
 * invoice.
 */
export const EndUserInvoicesDrawerContent: React.FC<
  EndUserInvoicesDrawerContentProps
> = ({ drawerConfig }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const closeDrawer = () => dispatch(RIGHT_DRAWER.HideDrawer());

  // Site picker scoped to the end-user's own sites (no navigation on select).
  const siteEntities = useHeaderEntities<any>({ forceEntityType: 'site' });
  const [localSelected, setLocalSelected] = useState<any | null>(null);
  const selected = localSelected ?? siteEntities.selected;

  const isAllSites = Boolean(selected?.__allSites);
  const selectedSites = useMemo(
    () =>
      isAllSites || !selected
        ? undefined
        : [{ flexo_site_id: siteEntities.getId(selected) }],
    [isAllSites, selected, siteEntities]
  );

  const subtitle =
    drawerConfig?.title ?? (t('dropdowns.profile.invoices') as string);

  return (
    <div
      className={`${styles.RightDrawerContent__Wrapper} ${styles.RightDrawerContent__InvoicesWrapper}`}
    >
      {/* FLEXO-6106: title (site selector) + "Invoices" subtitle stacked as a
          heading block, mirroring the shared drawer structure (see total-costs.tsx).
          The subtitle is a SIBLING of __Title — never a descendant — so the shared
          `.RightDrawerContent__Title p { 24px semibold }` rule can't clobber it. */}
      <div className={styles.RightDrawerContent__InvoicesHeading}>
        <div className={styles.RightDrawerContent__Title}>
          <HeaderEntityDropdown
            items={siteEntities.items}
            selected={selected}
            getLabel={siteEntities.getLabel}
            getId={siteEntities.getId}
            getDescription={siteEntities.getDescription}
            onSelect={(item: any) => setLocalSelected(item)}
            disabled={siteEntities.disabled}
            singleItem={siteEntities.singleItem}
            noOptionsText={siteEntities.noOptionsText}
            fullWidth={false}
            variant="invoices"
          />
          <DrawerCloseButton onClose={closeDrawer} ariaLabel={t('common.close', { defaultValue: 'Close' })} />
        </div>
        {/* Figma "Paragraph" token → shared `.paragraph` class; colour is the only
            per-modal bit (Grayscale/Dark Gray #505558 = greyscale-700). */}
        <p className={`paragraph ${styles.RightDrawerContent__InvoicesHeading__Subtitle}`}>
          {subtitle}
        </p>
      </div>
      <Billing
        view={isAllSites ? 'community' : 'site'}
        selectedSites={selectedSites}
      />
    </div>
  );
};

export default EndUserInvoicesDrawerContent;
